import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, FileText, Download, ShieldCheck, Building2, User, CheckCircle, XCircle, ClipboardList, ArrowLeft, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VigilanciaPage() {
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [verificarCodigo, setVerificarCodigo] = useState('');
  const [verificacaoResult, setVerificacaoResult] = useState(null);
  const [showVerificacao, setShowVerificacao] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Auto-verificar se veio da URL com ?verificar=CODIGO (QR Code do certificado)
  useEffect(() => {
    const codigoUrl = searchParams.get('verificar');
    if (codigoUrl) {
      setVerificarCodigo(codigoUrl.toUpperCase());
      fetch(`${API}/api/vigilancia/verificar/${codigoUrl}`)
        .then(res => res.json())
        .then(data => { setVerificacaoResult(data); setShowVerificacao(true); })
        .catch(() => { setVerificacaoResult({ valido: false, mensagem: 'Erro na verificação' }); setShowVerificacao(true); });
    }
  }, [searchParams]);

  const handleBusca = async (e) => {
    e.preventDefault();
    if (busca.trim().length < 3) {
      setErro('Digite pelo menos 3 caracteres');
      return;
    }
    setLoading(true);
    setErro('');
    setResultados(null);
    try {
      const res = await fetch(`${API}/api/vigilancia/consulta?busca=${encodeURIComponent(busca.trim())}`);
      const data = await res.json();
      if (data.total === 0) {
        setErro('Nenhum resultado encontrado para essa busca');
      } else {
        setResultados(data);
      }
    } catch {
      setErro('Erro ao consultar. Tente novamente.');
    }
    setLoading(false);
  };

  const handleVerificar = async () => {
    if (!verificarCodigo.trim()) return;
    try {
      const res = await fetch(`${API}/api/vigilancia/verificar/${verificarCodigo.trim()}`);
      const data = await res.json();
      setVerificacaoResult(data);
      setShowVerificacao(true);
    } catch {
      setVerificacaoResult({ valido: false, mensagem: 'Erro na verificação' });
      setShowVerificacao(true);
    }
  };

  const downloadPDF = (tipo, contratoId) => {
    window.open(`${API}/api/vigilancia/${tipo}/${contratoId}/pdf`, '_blank');
  };

  const statusColor = (status) => {
    const map = {
      ativo: 'bg-green-100 text-green-800 border-green-300',
      suspenso: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      encerrado: 'bg-slate-100 text-slate-800 border-slate-300',
      cancelado: 'bg-red-100 text-red-800 border-red-300',
      rascunho: 'bg-blue-100 text-blue-800 border-blue-300',
      aguardando_assinatura: 'bg-orange-100 text-orange-800 border-orange-300',
    };
    return map[status] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <div className="bg-[#1e3a5f] text-white">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Vigilância Sanitária</h1>
                <p className="text-blue-200 text-sm">Consulta Pública de Contratos de Esterilização</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="text-white hover:bg-white/10"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* Busca */}
        <Card className="shadow-lg border-0">
          <CardContent className="pt-6">
            <form onSubmit={handleBusca} className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Buscar por nome da empresa, CPF ou CNPJ..."
                    className="pl-10 h-12 text-lg"
                    data-testid="vigilancia-busca"
                  />
                </div>
                <Button type="submit" disabled={loading} className="h-12 px-8 bg-[#1e3a5f] hover:bg-[#2a4f7a]" data-testid="vigilancia-btn-buscar">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5 mr-2" />}
                  Consultar
                </Button>
              </div>
              {erro && <p className="text-red-500 text-sm">{erro}</p>}
            </form>
          </CardContent>
        </Card>

        {/* Verificação de certificado */}
        <Card className="border-0 shadow">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-600 mb-3 font-medium">Verificar autenticidade de certificado:</p>
            <div className="flex gap-3">
              <Input
                value={verificarCodigo}
                onChange={(e) => setVerificarCodigo(e.target.value.toUpperCase())}
                placeholder="Cole o código de verificação"
                className="font-mono uppercase"
                maxLength={12}
                data-testid="vigilancia-codigo"
              />
              <Button onClick={handleVerificar} variant="outline" data-testid="vigilancia-btn-verificar">
                <ShieldCheck className="w-4 h-4 mr-2" />
                Verificar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Resultados */}
        {resultados && (
          <div className="space-y-6">
            <p className="text-sm text-slate-500">{resultados.total} resultado{resultados.total > 1 ? 's' : ''} encontrado{resultados.total > 1 ? 's' : ''}</p>
            
            {resultados.clientes.map(cliente => (
              <Card key={cliente.id} className="border-0 shadow-lg overflow-hidden" data-testid={`resultado-cliente-${cliente.id}`}>
                <CardHeader className="bg-slate-50 border-b">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {cliente.tipo_pessoa === 'PJ' ? (
                        <Building2 className="w-6 h-6 text-[#1e3a5f]" />
                      ) : (
                        <User className="w-6 h-6 text-[#1e3a5f]" />
                      )}
                      <div>
                        <CardTitle className="text-lg">{cliente.nome}</CardTitle>
                        <p className="text-sm text-slate-500">
                          {cliente.tipo_pessoa === 'PJ' ? 'CNPJ' : 'CPF'}: {cliente.cnpj_cpf}
                        </p>
                      </div>
                    </div>
                    <div className="text-right text-sm text-slate-500">
                      <p>{cliente.total_contratos} contrato{cliente.total_contratos !== 1 ? 's' : ''}</p>
                      <p>{cliente.total_ciclos} ciclo{cliente.total_ciclos !== 1 ? 's' : ''} total</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  {cliente.contratos.length === 0 ? (
                    <p className="text-slate-400 text-center py-4">Nenhum contrato registrado</p>
                  ) : (
                    cliente.contratos.map(contrato => (
                      <div key={contrato.id} className="p-4 border rounded-lg space-y-3" data-testid={`contrato-${contrato.id}`}>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-500" />
                            <span className="font-bold">{contrato.numero_contrato}</span>
                            <Badge className={statusColor(contrato.status)}>
                              {contrato.status === 'ativo' && <CheckCircle className="w-3 h-3 mr-1" />}
                              {contrato.status === 'cancelado' && <XCircle className="w-3 h-3 mr-1" />}
                              {contrato.status.replace('_', ' ').toUpperCase()}
                            </Badge>
                            <Badge variant="outline">{contrato.tipo.replace('_', ' ').toUpperCase()}</Badge>
                          </div>
                          <span className="text-xs font-mono text-slate-400">COD: {contrato.codigo_verificacao}</span>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                          <div>
                            <p className="text-slate-500">Vigência</p>
                            <p className="font-medium">{contrato.data_inicio} a {contrato.data_fim}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Ciclos realizados</p>
                            <p className="font-medium">{contrato.ciclos_realizados}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Serviços</p>
                            <p className="font-medium">{contrato.servicos.length} serviço{contrato.servicos.length !== 1 ? 's' : ''}</p>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 pt-2 border-t">
                          <Button
                            size="sm"
                            onClick={() => downloadPDF('certificado', contrato.id)}
                            className="bg-[#1e3a5f] hover:bg-[#2a4f7a]"
                            data-testid={`btn-certificado-${contrato.id}`}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Certificado PDF
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadPDF('relatorio', contrato.id)}
                            data-testid={`btn-relatorio-${contrato.id}`}
                          >
                            <ClipboardList className="w-4 h-4 mr-2" />
                            Relatório de Esterilizações
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Dialog verificação */}
      <Dialog open={showVerificacao} onOpenChange={setShowVerificacao}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" />
              Verificação de Certificado
            </DialogTitle>
            <DialogDescription>Resultado da verificação do código informado</DialogDescription>
          </DialogHeader>
          {verificacaoResult && (
            <div className={`p-4 rounded-lg border ${verificacaoResult.valido ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              {verificacaoResult.valido ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-700 font-bold">
                    <CheckCircle className="w-5 h-5" />
                    CERTIFICADO VÁLIDO
                  </div>
                  <p className="text-sm"><strong>Contrato:</strong> {verificacaoResult.contrato}</p>
                  <p className="text-sm"><strong>Cliente:</strong> {verificacaoResult.cliente}</p>
                  <p className="text-sm"><strong>CPF/CNPJ:</strong> {verificacaoResult.cnpj_cpf}</p>
                  <p className="text-sm"><strong>Status:</strong> {verificacaoResult.status?.toUpperCase()}</p>
                  <p className="text-sm"><strong>Vigência:</strong> {verificacaoResult.vigencia}</p>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-700 font-bold">
                  <XCircle className="w-5 h-5" />
                  {verificacaoResult.mensagem || 'CERTIFICADO INVÁLIDO'}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
